/**
 * The reads on the new balance (M4S-5895, plan step 24): endpoints, query keys and the request
 * shapes for the balance, fleet, usage, activity, holds and the activity CSV.
 * docs/pdlc/credits-ui.md, "Proposed design" §2.4 and §7.
 *
 * Every client read is `/credit-consumption/**` (the caller's own org; D6-scoped on the server);
 * every staff read is `/admin/orgs/{orgId}/credit-consumption/**`, and `view: 'client'` returns
 * exactly what the client's org admin gets (View-as, and the client copy of the export).
 *
 * Kept apart from `constants.ts` so the CS actions (step 23) and these reads add their entries
 * without touching the same lines. The org balance key is the one step 23 invalidates after an
 * action (`QUERY_KEYS.ORG_BALANCE`), so both cache the same answer.
 */
import type { CreditBalance } from './balance';

/** `GET admin/credit-consumption/balances` (§2.4): the fleet, filtered on the server. */
export interface CreditFleetQuery {
    /** 'mine': orgs whose owner or CSM is the caller. */
    scope?: 'mine' | 'all';
    /** Org name contains (case-insensitive). */
    search?: string;
    /** Plan tier as today's fleet filters it. */
    plan?: string;
    /** Period for Used and Expired unused (ISO); default the month start to now. */
    from?: string;
    to?: string;
    /** Orgs with a CS adjustment under this reason code recorded in the period (step 29). */
    reasonCode?: string;
    /** Orgs with unused credits due to expire within 60 days (step 29). */
    expiring?: boolean;
}

/** Who looks after an org, beside the fleet's balances; never inside a `CreditBalance` (SH-4). */
export interface CreditFleetAccount {
    ownerEmail: string | null;
    csmEmail: string | null;
    /** MFour's own org: "No credit limit (MFour)". */
    internal: boolean;
}

/**
 * The fleet answer, as the API publishes it (`ledger/ledger-balance.service.ts`, `IFleetBalances`).
 * Every org, flipped or not; the summary follows scope, search and plan (§7 Fleet).
 */
export interface CreditFleetBalances {
    balances: CreditBalance[];
    /** By org id, for every org in `balances` (`IFleetAccount` in the API). */
    accounts: Record<string, CreditFleetAccount>;
    summary: {
        accounts: number;
        totalBalance: number;
        usedInPeriod: number;
        needAttention: number;
        from: string;
        to: string;
    };
}

/**
 * One change to the DANI conversion ratio (`snowflake_to_studio_ratio`), as
 * `GET admin/credit-consumption/ratio-changes` answers it (the API's `IRatioChange`), newest first.
 */
export interface CreditRatioChange {
    id: number;
    at: string;
    scope: 'global' | 'org';
    orgId: number | null;
    orgName: string | null;
    /** null on an org: it had (or now has) no ratio of its own, so the global one applies. */
    from: number | null;
    to: number | null;
    by: string;
    note: string | null;
}

/** `GET admin/credit-consumption/adjustments/csv`: every CS row in the period (staff only). */
export interface CreditAdjustmentsCsvQuery {
    from: string;
    to: string;
    reasonCode?: string;
}

/** The API's cap on one adjustments export (`AdjustmentHistoryQueryDto.limit`). */
export const ADJUSTMENTS_CSV_LIMIT = 5000;

/** `GET usage` and the base of every activity read: the period and the feature filters (§7). */
export interface CreditUsageQuery {
    /** ISO date or date-time, inclusive; usage defaults to this UTC month. */
    from?: string;
    /** ISO, exclusive; defaults to now. */
    to?: string;
    /** A feature's own cost, scoped like every row. */
    conversationId?: string;
    surveyId?: string;
    checkId?: string;
}

/** `GET activity` and `activity/export`. */
export interface CreditActivityQuery extends CreditUsageQuery {
    page?: number;
    /** 25 per page on screen (§7); the API allows up to 100. */
    limit?: number;
    /** A usage purpose; a correction counts under the purpose it corrects. */
    purpose?: string;
    /** Admins and staff: one person's rows (ignored for a member). */
    orgUserId?: number;
    provisionalOnly?: boolean;
    /**
     * Lines recorded at or after this instant (ISO), whatever their effective date. The Salesforce
     * feed back-dates a late record, so the CS page's interim-fix scan reads by this, never `from`.
     */
    recordedFrom?: string;
}

/** `GET activity/earlier`: today's activity list cut at T0. */
export interface CreditEarlierQuery {
    page?: number;
    limit?: number;
}

/** Activity's page size on screen (§7 block 8). */
export const ACTIVITY_PAGE_SIZE = 25;

/**
 * Whose credits a read is for.
 *
 * - `own`: the signed-in person's org, through the client endpoints.
 * - `org`: one org through the staff endpoints. `view: 'client'` asks for the client's own answer
 *   (View-as, and the CS page's client copy); without it, the internal answer.
 */
export type CreditReadTarget = { kind: 'own' } | { kind: 'org'; orgId: string; view?: 'client' };

export const LEDGER_READ_ENDPOINTS = {
    BALANCE: '/credit-consumption/balance',
    USAGE: '/credit-consumption/usage',
    ACTIVITY: '/credit-consumption/activity',
    ACTIVITY_EARLIER: '/credit-consumption/activity/earlier',
    ACTIVITY_EXPORT: '/credit-consumption/activity/export',
    HOLDS: '/credit-consumption/holds',
    FLEET_BALANCES: '/admin/credit-consumption/balances',
    RATIO_CHANGES: '/admin/credit-consumption/ratio-changes',
    ADJUSTMENTS_CSV: '/admin/credit-consumption/adjustments/csv',
    FLEET_REASON_CODES: '/admin/credit-consumption/adjustment-reasons',
    ORG_BALANCE: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/balance`,
    ORG_USAGE: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/usage`,
    ORG_ACTIVITY: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/activity`,
    ORG_ACTIVITY_EARLIER: (orgId: string) =>
        `/admin/orgs/${orgId}/credit-consumption/activity/earlier`,
    ORG_ACTIVITY_EXPORT: (orgId: string) =>
        `/admin/orgs/${orgId}/credit-consumption/activity/export`,
    ORG_HOLDS: (orgId: string) => `/admin/orgs/${orgId}/credit-consumption/holds`,
} as const;

/** The target as one key segment: `own`, or the org with its view. */
function targetKey(target: CreditReadTarget): readonly string[] {
    return target.kind === 'own' ? ['own'] : ['org', target.orgId, target.view ?? 'internal'];
}

export const LEDGER_READ_QUERY_KEYS = {
    /** The caller's own balance. */
    BALANCE: ['credit-consumption', 'balance'] as const,
    /** One org's balance: the same key step 23's CS actions invalidate. */
    ORG_BALANCE: (orgId: string) => ['credit-consumption', 'org', orgId, 'balance'] as const,
    FLEET_BALANCES: (query: CreditFleetQuery) =>
        [
            'credit-consumption',
            'balances',
            query.scope ?? 'all',
            query.search ?? '',
            query.plan ?? '',
            query.from ?? '',
            query.to ?? '',
            query.reasonCode ?? '',
            query.expiring ? 'expiring' : '',
        ] as const,
    RATIO_CHANGES: ['credit-consumption', 'ratio-changes'] as const,
    /** The same key step 23's reasons hook uses: one cached list. */
    FLEET_REASON_CODES: ['credit-consumption', 'adjustment-reasons'] as const,
    USAGE: (target: CreditReadTarget, query: CreditUsageQuery) =>
        ['credit-consumption', 'usage', ...targetKey(target), query] as const,
    ACTIVITY: (target: CreditReadTarget, query: CreditActivityQuery) =>
        ['credit-consumption', 'activity', ...targetKey(target), query] as const,
    ACTIVITY_EARLIER: (target: CreditReadTarget, query: CreditEarlierQuery) =>
        ['credit-consumption', 'activity-earlier', ...targetKey(target), query] as const,
    HOLDS: (target: CreditReadTarget) =>
        ['credit-consumption', 'holds', ...targetKey(target)] as const,
} as const;
